import type {
  IanaTimezoneString,
  ApiISODateString,
  ApiISOTimeString,
} from '#/api/types/ApiTypedDate';
import { useI18n } from '#/i18n/hooks/useI18n';
import { getUserTimezone } from '#/utils/timezones';
import { useIsClient } from '#/hooks/useIsClient';
import { humanIsoTimeString } from '#/utils/string/date/isoTimeString';
import { shortIanaTimezone } from '#/utils/string/date/ianaTimezone';
import { humanIsoDateString } from '#/utils/string/date/isoDateString';
import { ListingIsoDateAndTimeTimezoneTooltip } from '#/modules/listing/components/Date/ListingIsoDateAndTimeTimezoneTooltip';

type Props = {
  date: ApiISODateString;
  time:
    | {
        type: 'single';
        value: ApiISOTimeString;
        timezone: IanaTimezoneString;
      }
    | {
        type: 'range';
        start: ApiISOTimeString;
        end: ApiISOTimeString;
        timezone: IanaTimezoneString;
      }
    | null;
  qaId?: string;
};

export function ListingIsoDateAndTime({ date, time, qaId }: Props) {
  const { locale } = useI18n();

  const isClient = useIsClient();
  const userTimezone = getUserTimezone();

  const timeString = (() => {
    if (!time) return null;

    if (time.type === 'single')
      return humanIsoTimeString(time.value, { locale });

    const startTimeString = humanIsoTimeString(time.start, { locale });
    const endTimeString = humanIsoTimeString(time.end, { locale });
    return `${startTimeString} - ${endTimeString}`;
  })();

  const combinedDateTimeString = [
    humanIsoDateString(date, { locale, monthFormat: 'long' }),
    time && timeString,
  ]
    .filter(Boolean)
    .join(', ');

  const formattedDateTime = [
    combinedDateTimeString,
    time?.timezone && shortIanaTimezone(time.timezone, { date, locale }),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      data-qa-id={qaId}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
    >
      <div>{formattedDateTime}</div>

      {time && isClient && userTimezone !== time.timezone && (
        <ListingIsoDateAndTimeTimezoneTooltip
          date={date}
          startTime={time.type === 'single' ? time.value : time.start}
          endTime={time.type === 'range' ? time.end : null}
          fromTimezone={time.timezone}
          toTimezone={userTimezone}
        />
      )}
    </div>
  );
}
